import { Injectable, Signal, computed, inject } from '@angular/core';
import {
  PERMISSIONS,
  PermissionCode,
} from '@sbd-doug-frontend/shared/lib/constants/permissions.constants';
import { PERMISSIONS_SOURCE } from '@sbd-doug-frontend/shared/lib/injections/permissions-source-injection-token';

/**
 * Example (component):
 *   private _perms = inject(PermissionsService);
 *   canEditJsonConfig = this._perms.canEditJsonConfig;
 *
 * Example (template):
 *   <textarea [readonly]="!perms.canEditJsonConfig()"></textarea>
 */
@Injectable({ providedIn: 'root' })
export class PermissionsService {
  private _permissionsSource = inject(PERMISSIONS_SOURCE);

  private _permissions = computed(() => this._permissionsSource() ?? []);

  has(permission: PermissionCode | string): boolean {
    const code = permission?.toLowerCase();
    return this._permissions().some((p) => p?.toLowerCase() === code);
  }

  // ---------------------------------------------------------------------------
  // Advanced Configuration vTab
  // ---------------------------------------------------------------------------

  /**
   * Can view advanced configuration vTabs.
   * Having edit always implies view.
   */
  canViewAdvancedConfig: Signal<boolean> = computed(
    () =>
      this.has(PERMISSIONS.advancedConfigView) ||
      this.has(PERMISSIONS.advancedConfigEdit)
  );

  /** Can edit advanced configuration settings. */
  canEditAdvancedConfig: Signal<boolean> = computed(() =>
    this.has(PERMISSIONS.advancedConfigEdit)
  );

  // ---------------------------------------------------------------------------
  // JSON Configuration Textarea
  // ---------------------------------------------------------------------------

  /** Can view / edit any JSON configuration textarea. */
  canEditJsonConfig: Signal<boolean> = computed(() =>
    this.has(PERMISSIONS.jsonConfigEdit)
  );
}
